import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createView } from '../../slices/View/CreateView/thunk';
import { Button, FormGroup, Label, Input, Col, Card, Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { CreatableSelectField } from './CreatableSelectField';
import { deleteView } from '../../slices/View/DeleteView/thunk';
import { hasPermission } from './UtilityFunctions';

export const useColumnSettings = (pageName, columns) => {
    const dispatch = useDispatch();
    const view = useSelector((state) => state.View.getView);
    const userId = localStorage.getItem("userName");
    const [selectedView, setSelectedView] = useState('');
    const [viewColumns, setViewColumns] = useState([]);
    const [settingsModalOpen, setSettingsModalOpen] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState({});
    const [loading, setLoading] = useState(false);
    const [defaultView, setDefaultView] = useState(false);
    const [isCreatingView, setIsCreatingView] = useState(false);
    const [isInitialized, setIsInitialized] = useState(false);
    const rawPermissions = useSelector(
        (state) => state.permissions.permissionsMap
    );

    const permissionsMap = new Map(
        rawPermissions.map(([key, value]) => [key, new Set(value)])
        
    );
    const viewPermission = hasPermission(permissionsMap, "miscellaneous", "view")

    useEffect(() => {
        if (!isInitialized && columns && columns.length > 0) {
            const initialVisibleColumns = {};
            columns.forEach(column => {
                const key = column.accessorKey || column.header;
                initialVisibleColumns[key] = true;
            });
            setVisibleColumns(initialVisibleColumns);
            setViewColumns(columns.map(col => col.accessorKey || col.header));
            setIsInitialized(true);
        }
    }, [columns, isInitialized]);

    // Handle view data changes - only run when view changes
    useEffect(() => {
        if (!view || !isInitialized) return;

        const currentPage = view.pages?.find(page => page.pageName === pageName);
        if (!currentPage?.views?.length) return;

        const defaultViewData = currentPage.views.find(v => v.defaultView);
        const initialView = defaultViewData || currentPage.views[0];
        if (initialView) {
            setSelectedView(initialView.viewName);
            setViewColumns(initialView.viewColumns);
            setDefaultView(initialView.defaultView);

            // Update visible columns
            const updatedVisibleColumns = {};
            columns.forEach(column => {
                const key = column.accessorKey || column.header;
                updatedVisibleColumns[key] = initialView.viewColumns.includes(key);
            });
            setVisibleColumns(updatedVisibleColumns);

            setIsCreatingView(false);
        }
    }, [view, isInitialized]); // Don't include columns or pageName to avoid loops

    const toggleSettingsModal = () => {
        const newState = !settingsModalOpen;
        setSettingsModalOpen(newState);

        // if (newState) {
        //     // Opening the modal - reset to create new view
        //     setSelectedView('');

        //     // Reset columns to all visible
        //     const resetColumns = {};
        //     columns.forEach(column => {
        //         const key = column.accessorKey || column.header;
        //         resetColumns[key] = true;
        //     });
        //     setVisibleColumns(resetColumns);

        //     setViewColumns(columns.map(col => col.accessorKey || col.header));
        //     setDefaultView(false);
        //     setIsCreatingView(true);
        // }
    };

    const handleColumnVisibilityChange = (column) => {
        setVisibleColumns(prev => ({
            ...prev,
            [column]: !prev[column]
        }));
    };

    const handleSaveSettings = () => {
        const visibleColumnsKeys = columns
            .filter(column => visibleColumns[column.accessorKey || column.header])
            .map(column => column.accessorKey || column.header);
    
        const validPageNames = ['uom', 'resource', 'attribute', 'service', 'baseOffer', 'addOnOffer', 'customer',"userRoles"];
        const isValidPage = validPageNames.includes(pageName);
        if (isValidPage && !visibleColumnsKeys.includes('checkbox')) {
            visibleColumnsKeys.push('checkbox');
        }

        const saveData = {
            pagename: pageName,
            viewName: selectedView,
            columns: visibleColumnsKeys.length ? visibleColumnsKeys : viewColumns,
            defaultView: defaultView
        };

        dispatch(createView(saveData, setLoading));
        setViewColumns(saveData.columns);
        setSettingsModalOpen(false);
    };

    // This is the key function where we need to update the visible columns
    const handleViewChange = (selectedOption) => {
        const viewName = selectedOption?.value || '';
        setSelectedView(viewName);

        // Find the selected view data
        const selectedViewData = view?.pages
            ?.find(page => page.pageName === pageName)
            ?.views?.find(v => v.viewName === viewName);

        if (!selectedViewData) {
            // Creating new view - set all columns visible
            setIsCreatingView(true);

            const resetColumns = {};
            columns.forEach(column => {
                const key = column.accessorKey || column.header;
                resetColumns[key] = true;
            });
            setVisibleColumns(resetColumns);

            setViewColumns(columns.map(col => col.accessorKey || col.header));
            setDefaultView(false);
        } else {
            // Selected existing view - set columns based on viewColumns
            setViewColumns(selectedViewData.viewColumns);
            setDefaultView(selectedViewData.defaultView);
            setIsCreatingView(false);

            // THIS IS THE CRITICAL PART - update visible columns based on the selected view
            const updatedVisibleColumns = {};
            columns.forEach(column => {
                const key = column.accessorKey || column.header;
                updatedVisibleColumns[key] = selectedViewData.viewColumns.includes(key);
            });
            setVisibleColumns(updatedVisibleColumns);
        }
    };

    const handleDeleteView = (viewName, e) => {
        if (e) e.stopPropagation();

        // Reset to create new view
        setSelectedView('');

        const resetColumns = {};
        columns.forEach(column => {
            const key = column.accessorKey || column.header;
            resetColumns[key] = true;
        });
        setVisibleColumns(resetColumns);

        setViewColumns(columns.map(col => col.accessorKey || col.header));
        setIsCreatingView(true);
        dispatch(deleteView(userId, setLoading, viewName));
    };

    const handleDefaultViewChange = () => {
        setDefaultView(prev => !prev);
    };

    const SettingsSidebar = () => (
        <Col lg={2} className={`${settingsModalOpen ? '' : 'd-none'} rounded py-4 bg-white`}>
            {/* <Card className="mb-4 h-100 w-100 p-4"> */}
            <div>

                <FormGroup>
                    <Label for="viewSelect">Select or Create View</Label>
                    <CreatableSelectField
                        name="viewSelect"
                        options={(view?.pages?.find(page => page.pageName === pageName)?.views || []).map(v => ({
                            value: v.viewName,
                            label: (
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        {v.viewName}
                                        {v.defaultView && (
                                            <span className="ms-2 badge bg-success">Default</span>
                                        )}
                                    </div>
                                    {viewPermission && viewPermission?.has("delete") &&
                                        <Button
                                            className="btn-sm ms-2"
                                            color="danger"
                                            onClick={(e) => handleDeleteView(v.viewName, e)}
                                        >
                                            <i className="ri-delete-bin-line"></i>
                                        </Button>}
                                </div>
                            )
                        }))}
                        isMulti={false}
                        value={selectedView ? { value: selectedView, label: selectedView } : null}
                        handleChange={handleViewChange}
                        placeholder="Select View"
                    />
                </FormGroup>

                <FormGroup>
                    <Label>Columns Visibility</Label>
                    {columns.map(column => (
                        column.accessorKey !== "checkbox" ? (
                            <div key={column.accessorKey || column.header} className="form-check form-switch">
                                <Input
                                    type="switch"
                                    id={column.accessorKey === "status" ? "Status" : column.accessorKey || column.header}
                                    checked={visibleColumns[column.accessorKey || column.header] || false}
                                    onChange={() => handleColumnVisibilityChange(column.accessorKey || column.header)}
                                    className="rv-form-switch-input"
                                />
                                <Label for={column.accessorKey || column.header} className="form-check-label">
                                    {column.header}
                                </Label>
                            </div>
                        ) : null
                    ))}
                </FormGroup>

                <FormGroup>
                    <Input
                        type="checkbox"
                        id="defaultViewCheckbox"
                        checked={defaultView}
                        onChange={handleDefaultViewChange}
                        className="me-2"
                    />
                    <Label for="defaultViewCheckbox" className="form-check-label">Set as Default View</Label>
                </FormGroup>

                <Button color="secondary" className="me-2" onClick={toggleSettingsModal}>Cancel</Button>

                <Button color="main-color" onClick={handleSaveSettings} disabled={!(viewPermission && viewPermission?.has("create"))}>Save</Button>
            </div>
            {/* </Card> */}
        </Col>
    );

    const CustomViewModal = () => (
        <Modal
            backdrop="static"
            centered={true}
            isOpen={settingsModalOpen}
            style={{width: '400px'}}
        >
            <ModalHeader
                // toggle={toggle}
                className="fs-3 fw-bold justify-content-center rv-text-color p-3"
            >
                {"Custom View"}
                <button
                    type="button"
                    className="close-button-currency position-absolute top-0 end-0 mt-1 me-3 border-0 bg-transparent fs-3"
                    onClick={toggleSettingsModal}
                >
                    &times;
                </button>
            </ModalHeader>
            <ModalBody className="justify-content-start p-4 w-100 pb-0">
                <div className='px-3'>
                    <FormGroup>
                        <Label for="viewSelect" className='fw-bold fs-5 rv-text-color'>Select or Create a View</Label>
                        <Label for="viewSelect" className='text-muted'>Type to create a new option</Label>
                        <CreatableSelectField
                            name="viewSelect"
                            className={"w-100 bg-white"}
                            options={(view?.pages?.find(page => page.pageName === pageName)?.views || []).map(v => ({
                                value: v.viewName,
                                label: (
                                    <div className="d-flex justify-content-between align-items-center rv-text-color">
                                        <div>
                                            {v.viewName}
                                            {v.defaultView && (
                                                <span className="ms-2 badge bg-secondary">Default</span>
                                            )}
                                        </div>
                                        {viewPermission && viewPermission?.has("delete") &&
                                            <Button
                                                className="btn btn-sm rounded-circle text-danger border-danger bg-white"
                                                style={{ width: "17px", height: "17px", fontSize: "12px", padding: "0", lineHeight: "1", fontWeight: "bold", border: "2px solid red" }}
                                                color="danger"
                                                onClick={(e) => handleDeleteView(v.viewName, e)}
                                            >
                                                <i className="ri-close-line"></i>
                                            </Button>}
                                    </div>
                                )
                            }))}
                            isMulti={false}
                            value={selectedView ? { value: selectedView, label: selectedView } : null}
                            handleChange={handleViewChange}
                            onCreateOption={(newValue) => {handleViewChange({label: newValue, value: newValue})}}
                            placeholder="Select View"
                            isRVStyle={true}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label className='fw-bold fs-5 rv-text-color pt-2'>Column Visibility</Label>
                        {columns.map(column => (
                            column.accessorKey !== "checkbox" ? (
                                <div key={column.accessorKey || column.header} className="form-check form-switch pb-2" style={{fontSize: '12px'}}>
                                    <Input
                                        type="switch"
                                        id={column.accessorKey === "status" ? "Status" : column.accessorKey || column.header}
                                        checked={visibleColumns[column.accessorKey || column.header] || false}
                                        onChange={() => handleColumnVisibilityChange(column.accessorKey || column.header)}
                                        className="rv-form-switch-input"
                                    />
                                    <Label for={column.accessorKey || column.header} className="form-check-label rv-text-color ps-1" style={{fontSize: '14px'}}>
                                        {column.header}
                                    </Label>
                                </div>
                            ) : null
                        ))}
                    </FormGroup>

                    <FormGroup>
                        <Input
                            type="checkbox"
                            id="defaultViewCheckbox"
                            checked={defaultView}
                            onChange={handleDefaultViewChange}
                            className="rv-table-checkbox me-2"
                        />
                        <Label for="defaultViewCheckbox" className="form-check-label">Set as Default View</Label>
                    </FormGroup>
                </div>
            </ModalBody>
            <ModalFooter className="d-flex justify-content-center align-items-center p-4">
                <div className="d-flex flex-row gap-3">

                    <Button
                        onClick={toggleSettingsModal}
                        className="btn-label rv-secondary-btn rounded-1 px-1 ps-4 py-1 w-100 w-md-auto text-nowrap"
                    >
                        <i className="ri-close-fill label-icon secondary-label-icon align-middle fs-16 me-1" />

                        <span className="ps-3 px-2 ms-2 me-1 modal-btn fw-bold">Close</span>
                    </Button>
                    <Button
                        className="btn-label rv-primary-btn rounded-1 px-1 ps-4 py-1 w-100 w-md-auto text-nowrap"
                        onClick={handleSaveSettings}
                        disabled={!(viewPermission && viewPermission?.has("create"))}
                    >
                        <i className="ri-save-line label-icon align-middle fs-16 me-1"></i>
                        <span className="ps-3 px-2 ms-2 me-1 modal-btn fw-bold">Save</span>
                    </Button>
                </div>
            </ModalFooter>
        </Modal>
    );

    return {
        settingsModalOpen,
        visibleColumns,
        selectedView,
        viewColumns,
        toggleSettingsModal,
        handleColumnVisibilityChange,
        handleSaveSettings,
        handleViewChange,
        SettingsSidebar,
        CustomViewModal
    };
};